import React, { useCallback, useEffect, useMemo, useState, Suspense, lazy, memo } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Routes, Route, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AnimatePresence, motion } from 'framer-motion';
import { queryClient } from './lib/queryClient';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { SEO } from './components/shared/SEO';
import { Header } from './components/layout/Header';
import { AnnouncementBar } from './components/layout/AnnouncementBar';
import { MobileDeliverToPill } from './components/layout/MobileDeliverToPill';
import { HeroBanner } from './components/home/HeroBanner';
import { PromoBanners } from './components/home/PromoBanners';
import { ProductSection } from './components/home/ProductSection';
import { Footer } from './components/layout/Footer';
import { MobileAppPromo } from './components/home/MobileAppPromo';
import { MultiBanner } from './components/home/MultiBanner';
import { DoubleBanner } from './components/home/DoubleBanner';
import { MobileFooterNav } from './components/layout/MobileFooterNav';
import { AuthModal } from './components/modals/AuthModal';
import { PrescriptionUpload } from './components/prescription/PrescriptionUpload';
import { InsurancePrescription } from './components/prescription/InsurancePrescription';
import { PharmacistChat } from './components/services/PharmacistChat';
import { QuickRefill } from './components/services/QuickRefill';
import { ShopByCategory } from './components/home/ShopByCategory';
import { TopOffers } from './components/home/TopOffers';
import { PrescriptionService } from './components/prescription/PrescriptionService';
import { LoadingSpinner } from './components/shared/LoadingSpinner';
import { trackPageView } from './lib/analytics/events';
import { MobileHomeHeader } from './components/MobileHomeHeader';
import { LocationModal } from './components/modals/LocationModal';
import { LazySection } from './components/shared/LazySection';
import { AppDownloadBottomSheet } from './components/modals/AppDownloadBottomSheet';
import { LocationPermissionModal } from './components/modals/LocationPermissionModal';
import { ProductsShowcaseSection } from './components/shared/ProductsShowcaseSection';
import { SectionSkeleton } from './components/shared/SkeletonLoader';
import { healthProducts, babyProducts } from './data/products';
import { BrandStorePage } from './pages/BrandStorePage';
import { haptics } from './lib/utils/haptics';
import { useStore } from './lib/store';
import { FeatureCardsSection } from './components/home/FeatureCardsSection';
import { PullToRefresh } from './components/shared/PullToRefresh';
// Lazy loaded components with prefetch
const CheckoutPage = lazy(() => import('./pages/CheckoutPage').then(module => ({
  default: module.CheckoutPage
})));
const PersonalizedQuiz = lazy(() => import('./components/services/PersonalizedQuiz').then(module => ({
  default: module.PersonalizedQuiz
})));
const MedicationFinder = lazy(() => import('./components/services/MedicationFinder').then(module => ({
  default: module.MedicationFinder
})));
const DoctorConsultation = lazy(() => import('./components/services/DoctorConsultation').then(module => ({
  default: module.DoctorConsultation
})));
const FlashSales = lazy(() => import('./components/home/FlashSales').then(module => ({
  default: module.FlashSales
})));
const RecommendedProducts = lazy(() => import('./components/home/RecommendedProducts').then(module => ({
  default: module.RecommendedProducts
})));
const TrendingProducts = lazy(() => import('./components/home/TrendingProducts').then(module => ({
  default: module.TrendingProducts
})));
const EnhancedClearanceSection = lazy(() => import('./components/home/EnhancedClearanceSection').then(module => ({
  default: module.EnhancedClearanceSection
})));
const CategorySpecificShowcase = lazy(() => import('./components/home/CategorySpecificShowcase').then(module => ({
  default: module.CategorySpecificShowcase
})));
const BrandsShowcase = lazy(() => import('./components/home/BrandsShowcase').then(module => ({
  default: module.BrandsShowcase
})));
const DealOfTheDay = lazy(() => import('./components/home/DealOfTheDay').then(module => ({
  default: module.DealOfTheDay
})));
const FAQ = lazy(() => import('./components/services/FAQ').then(module => ({
  default: module.FAQ
})));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage').then(module => ({
  default: module.ThankYouPage
})));
// Memoized fallback component
const SectionFallback = memo(() => <SectionSkeleton data-id="element-0" data-mp="3ko2rwp25r2v5" />);
import { generateOrganizationJsonLd, generateWebsiteJsonLd } from './utils/seo/jsonLd';
export function App() {
  const location = useLocation();
  const {
    language
  } = useStore();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [showAppDownload, setShowAppDownload] = useState(false);
  const [showLocationPermission, setShowLocationPermission] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeSection, setActiveSection] = useState('all');
  // Update active section based on scroll position
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-120px 0px -40% 0px',
      threshold: 0
    };
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const sections = ['trending-now', 'flash-sales', 'recommended-products', 'personalized-quiz', 'recently-viewed', 'mother-baby-essentials'];
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    // Special case for "all" (top of page)
    const handleScroll = () => {
      if (window.scrollY < 100) {
        setActiveSection('all');
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  // Register service worker for offline support
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js').catch(() => {
          // Service worker registration failed
        });
      });
    }
  }, []);
  useEffect(() => {
    trackPageView('home');
  }, []);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.openAuthModal = () => setAuthModalOpen(true);
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.openAuthModal;
      }
    };
  }, []);
  // Check if modals should be shown on first visit
  useEffect(() => {
    const hasSeenAppDownload = localStorage.getItem('hasSeenAppDownload');
    const hasGrantedLocation = localStorage.getItem('hasGrantedLocation');
    // Show location permission modal first (both desktop and mobile)
    if (!hasGrantedLocation) {
      setTimeout(() => {
        setShowLocationPermission(true);
      }, 1000);
    }
    // Show app download bottom sheet only on mobile, after location is handled
    if (!hasSeenAppDownload && window.innerWidth < 768 && hasGrantedLocation) {
      setTimeout(() => {
        setShowAppDownload(true);
      }, 2000);
    }
  }, []);
  const handleLocationModalOpen = useCallback(() => {
    setLocationModalOpen(true);
  }, []);
  const handleLocationModalClose = useCallback(() => {
    setLocationModalOpen(false);
  }, []);
  const handleLocationSelect = useCallback((location: {
    name: string;
  }) => {
    setSelectedLocation(location);
    setLocationModalOpen(false);
  }, []);
  const handleAuthModalClose = useCallback(() => {
    setAuthModalOpen(false);
  }, []);
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);
  const scrollToSection = useCallback((sectionId: string) => {
    if (sectionId === 'all') {
      scrollToTop();
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 120; // Account for fixed header
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, [scrollToTop]);
  const handleAppDownloadClose = () => {
    localStorage.setItem('hasSeenAppDownload', 'true');
    setShowAppDownload(false);
  };
  const handleAppDownloadContinue = () => {
    localStorage.setItem('hasSeenAppDownload', 'true');
    setShowAppDownload(false);
  };
  const handleLocationPermissionClose = () => {
    localStorage.setItem('hasGrantedLocation', 'denied');
    setShowLocationPermission(false);
  };
  const handleLocationPermissionAllow = () => {
    setShowLocationPermission(false);
    // Request browser location permission
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        console.log('Location granted:', position.coords);
        localStorage.setItem('hasGrantedLocation', 'true');
        // Optionally set a default location based on coordinates
        // You could reverse geocode here or set a detected location
        setSelectedLocation({
          name: 'Current Location'
        });
      }, error => {
        console.log('Location denied:', error);
        localStorage.setItem('hasGrantedLocation', 'denied');
        // Show location modal if browser permission was denied
        // so user can manually select location
        setTimeout(() => {
          setLocationModalOpen(true);
        }, 300);
      });
    } else {
      // Geolocation not available, show manual location selector
      localStorage.setItem('hasGrantedLocation', 'denied');
      setTimeout(() => {
        setLocationModalOpen(true);
      }, 300);
    }
  };
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsRefreshing(false);
    window.location.reload();
  };
  const homeJsonLd = [generateOrganizationJsonLd(), generateWebsiteJsonLd()];
  return <ErrorBoundary data-id="element-1" data-mp="1ioz8p3fbf5h3">
      <QueryClientProvider client={queryClient} data-id="element-2" data-mp="1iofrxq1ein4n">
        <HelmetProvider data-id="element-3" data-mp="1imw7dn8pppx2">
          <div dir={language === 'ar' ? 'rtl' : 'ltr'} data-id="element-4" data-mp="3vz8po537tceh">
            <Toaster position="bottom-center" toastOptions={{
            style: {
              background: '#ffffff',
              color: '#1f2937',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '14px 16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              minWidth: '320px'
            },
            className: 'mb-20 md:mb-4',
            success: {
              style: {
                background: '#f0fdf4',
                color: '#166534',
                border: '1px solid #bbf7d0'
              },
              iconTheme: {
                primary: '#22c55e',
                secondary: '#ffffff'
              }
            },
            error: {
              style: {
                background: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fecaca'
              },
              iconTheme: {
                primary: '#ef4444',
                secondary: '#ffffff'
              }
            },
            loading: {
              style: {
                background: '#eff6ff',
                color: '#1e40af',
                border: '1px solid #bfdbfe'
              },
              iconTheme: {
                primary: '#3b82f6',
                secondary: '#ffffff'
              }
            }
          }} data-id="element-5" data-mp="3vypi9ib6y6ep" />
            <Suspense fallback={<LoadingSpinner data-id="element-7" data-mp="2pjje2xzy8jqv" />} data-id="element-6" data-mp="2pl490oq6e4p9">
              <AnimatePresence mode="wait" data-id="element-8" data-mp="2tmz95pwdec1g">
                <Routes location={location} key={location.pathname} data-id="element-9" data-mp="2tlo47mr6uy8r">
                  <Route path="/" element={<motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-11" data-mp="3m41tgxyglbw8">
                        <SEO title="Fast Delivery Across Saudi Arabia" description="Shop online at Innova Pharmacy for authentic health and beauty products with fast delivery across Saudi Arabia. Secure checkout, great prices, and 24/7 support." canonical="/en/" hreflangAr="/" hreflangEn="/en/" hreflangDefault="/" keywords="pharmacy Saudi Arabia, online pharmacy, health products, beauty products, medications delivery, vitamins supplements, medical devices, skincare, Riyadh pharmacy, Jeddah pharmacy, fast delivery pharmacy" jsonLd={homeJsonLd} data-id="element-12" data-mp="1fhcjn6ff9v3i" />
                        <div className="flex flex-col min-h-screen bg-gray-50 overflow-x-hidden" data-id="element-13" data-mp="mmg0ycnuhuwl">
                          <div className="hidden md:block" data-id="element-14" data-mp="qlyf8pl5v7qg">
                            <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} showAnnouncement={true} data-id="element-15" data-mp="vmurbqfo206y" />
                          </div>
                          <main className="flex-grow overflow-x-hidden pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-16" data-mp="1ecklcbbluhxt">
                            <h1 className="sr-only" data-id="element-17" data-mp="1ob9tvdhm2q1e">
                              Innova Pharmacy - Your Trusted Online Healthcare
                              Store
                            </h1>
                            <div className="md:hidden relative" data-id="element-18" data-mp="1tg3wvyj6cswx">
                              <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={handleLocationModalOpen} transparentAtTop={true} data-id="element-19" data-mp="1tesqrs29b92n" />
                              {/* Mobile Hero Banner connected to transparent header */}
                              <div data-id="element-20" data-mp="2c7myc0xjvywu">
                                <HeroBanner data-id="element-21" data-mp="2c8oh6rkt5pp4" />
                              </div>
                            </div>

                            {/* Desktop Hero Banner Section - Connected to header */}
                            <div className="hidden md:block" data-id="element-22" data-mp="23c73bgl27dpo">
                              <div className="container mx-auto px-4 pt-8 pb-6" data-id="element-23" data-mp="2zwzexlv7j996">
                                <HeroBanner data-id="element-24" data-mp="2v57jawx72dc6" />
                              </div>
                            </div>

                            {/* Sticky Category Filter Pills - Mobile Only */}
                            <div className="md:hidden sticky top-[56px] z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 px-4 py-2.5 overflow-x-auto scrollbar-hide" data-id="element-25" data-mp="y79zh7znr6rq">
                              <div className="flex gap-2" data-id="element-26" data-mp="oh7636omjqsv">
                                {[{
                            id: 'all',
                            label: 'All',
                            sectionId: 'all'
                          }, {
                            id: 'trending',
                            label: 'Trending',
                            sectionId: 'trending-now'
                          }, {
                            id: 'deals',
                            label: 'Best Deals',
                            sectionId: 'flash-sales'
                          }, {
                            id: 'new',
                            label: 'New Arrivals',
                            sectionId: 'recommended-products'
                          }, {
                            id: 'wellness',
                            label: 'Wellness',
                            sectionId: 'personalized-quiz'
                          }, {
                            id: 'skincare',
                            label: 'Skincare',
                            sectionId: 'recently-viewed'
                          }, {
                            id: 'baby',
                            label: 'Baby Care',
                            sectionId: 'mother-baby-essentials'
                          }].map((cat, idx) => <button key={cat.id} onClick={() => {
                            haptics.selection();
                            scrollToSection(cat.sectionId);
                          }} className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${idx === 0 ? 'bg-[#1E2B55] text-white shadow-md shadow-blue-900/20' : 'bg-gray-100 text-gray-600 active:bg-gray-200'}`} data-id="element-27" data-mp="1wuas01h8ftqj" data-mpi={idx}>
                                    {cat.label}
                                  </button>)}
                              </div>
                            </div>

                            <PullToRefresh onRefresh={handleRefresh} data-id="element-28" data-mp="2z919050hmb64">
                              <div className="py-1 md:py-4" data-id="element-29" data-mp="nnikihqfgxbn">
                                <MultiBanner data-id="element-30" data-mp="np3ea54x4c8g" />
                              </div>
                              <div className="py-1 md:py-4" data-id="element-31" data-mp="iopjhosbuysd">
                                <ShopByCategory data-id="element-32" data-mp="dwfmjossoygl" />
                              </div>
                              <div className="py-1 md:py-4" data-id="element-33" data-mp="8ye5taiudb8a">
                                <TopOffers data-id="element-34" data-mp="8w0ar1o5vz0q" />
                              </div>
                              <div className="py-1 md:py-4" data-id="element-35" data-mp="40kyuf9psd6y">
                                <FeatureCardsSection data-id="element-36" data-mp="3w2aqct7nnobi" />
                              </div>
                              <LazySection data-id="element-37" data-mp="3r3ha4t0zhj7n">
                                <div id="flash-sales" className="py-1 md:py-4" data-id="element-38" data-mp="3r497o9e55r0k">
                                  <Suspense fallback={<SectionFallback data-id="element-40" data-mp="3m67g102ngjdh" />} data-id="element-39" data-mp="3m5o6a6n5l1aj">
                                    <FlashSales data-id="element-41" data-mp="1pcw5vt7gzz8q" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <LazySection data-id="element-42" data-mp="1kbggjoy9f6kj">
                                <div id="mother-baby-essentials" className="py-1 md:py-4" data-id="element-43" data-mp="2c6axbjidobgl">
                                  <Suspense fallback={<SectionFallback data-id="element-45" data-mp="22g82ddrp4nfm" />} data-id="element-44" data-mp="22grf8g2holms">
                                    <CategorySpecificShowcase title="Mother & Baby Essentials" subtitle="CARING FOR YOUR LITTLE ONES" description="Safe, trusted, and essential products for everyday care of mothers and babies" viewAllLink="#" backgroundColor="bg-gradient-to-r from-pink-50 via-blue-50 to-pink-50" textColor="text-gray-900" iconColor="text-pink-500" products={babyProducts} icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-47" data-mp="3hdu6dl376ltl">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" data-id="element-48" data-mp="3v84txz3gk9y2" />
                                        </svg>} data-id="element-46" data-mp="1xiprhhu4by4z" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <LazySection data-id="element-49" data-mp="22za8bujh31t">
                                <div id="personalized-quiz" className="py-1 md:py-4" data-id="element-50" data-mp="3t88r9e0k26za">
                                  <Suspense fallback={<SectionFallback data-id="element-52" data-mp="71akgnoqxfkk" />} data-id="element-51" data-mp="6zpr31e642oa">
                                    <PersonalizedQuiz data-id="element-53" data-mp="3fg2dtzosee3j" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <div className="py-1 md:py-4" data-id="element-54" data-mp="3aev620mn9ewv">
                                <DoubleBanner data-id="element-55" data-mp="3p6x8nsg3mm0l" />
                              </div>
                              <div className="py-1 md:py-4" data-id="element-56" data-mp="3kevqjpw9212n">
                                <PromoBanners data-id="element-57" data-mp="3kdaufvuaea2o" />
                              </div>
                              <LazySection data-id="element-58" data-mp="2wnq9j7ekp2qr">
                                <div className="py-1 md:py-4" data-id="element-59" data-mp="2rqzzb0pg91d9">
                                  <Suspense fallback={<SectionFallback data-id="element-61" data-mp="178pez4s7x59z" />} data-id="element-60" data-mp="2roni4primv19">
                                    <DealOfTheDay data-id="element-62" data-mp="176updd604ozk" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <LazySection data-id="element-63" data-mp="xbaxd1uk6ag3">
                                <div className="py-1 md:py-4" data-id="element-64" data-mp="x8eztemmhns6">
                                  <Suspense fallback={<SectionFallback data-id="element-66" data-mp="129m7ldormmyu" />} data-id="element-65" data-mp="12a4ed4920jl0">
                                    <BrandsShowcase data-id="element-67" data-mp="1pynnflzxh0e5" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <LazySection data-id="element-68" data-mp="1gyip01cppcc4">
                                <div className="py-1 md:py-4" data-id="element-69" data-mp="1gxh4l69t3dhq">
                                  <Suspense fallback={<SectionFallback data-id="element-71" data-mp="1kyswjig5kat3" />} data-id="element-70" data-mp="1kzaymvphxj8x">
                                    <CategorySpecificShowcase title="Sports Performance Supplements" subtitle="PERFORMANCE BOOSTERS" description="Advanced formulations to enhance your workout intensity, endurance, and recovery" viewAllLink="#" backgroundColor="bg-gradient-to-r from-[#1E2B55] to-[#162041]" textColor="text-white" iconColor="text-[#1E2B55]" products={babyProducts} icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-73" data-mp="13rbfeccfa8gn">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" data-id="element-74" data-mp="3jkac7whrgola" />
                                        </svg>} data-id="element-72" data-mp="5j3h42iyt028" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <LazySection data-id="element-75" data-mp="2dpb66a83iqlt">
                                <div id="recommended-products" className="py-1 md:py-4" data-id="element-76" data-mp="2iofgj46raudq">
                                  <Suspense fallback={<SectionFallback data-id="element-78" data-mp="2rplxfedbi77i" />} data-id="element-77" data-mp="2rp3s7s8oging">
                                    <RecommendedProducts data-id="element-79" data-mp="2rn9f303ndutx" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <LazySection data-id="element-80" data-mp="xbl0iz1bvua2">
                                <div className="py-1 md:py-4" data-id="element-81" data-mp="20lyw7o4yrjuy">
                                  <Suspense fallback={<SectionFallback data-id="element-83" data-mp="1vn5f7lqgxaq1" />} data-id="element-82" data-mp="20ocmljop87w6">
                                    <EnhancedClearanceSection data-id="element-84" data-mp="1voq9da8vernd" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <LazySection data-id="element-85" data-mp="24n28k0nsyxrf">
                                <div className="py-1 md:py-4" data-id="element-86" data-mp="24q6jrhzoqtrl">
                                  <MobileAppPromo data-id="element-87" data-mp="2jjjiqok9a6ce" />
                                </div>
                              </LazySection>
                              <div className="py-1 md:py-4" data-id="element-88" data-mp="2ek6vw3tdwz9t">
                                <div className="container mx-auto px-4" data-id="element-89" data-mp="2sc4q78ifsqlx">
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4" data-id="element-90" data-mp="2obkqbx6hb8w3">
                                    <div className="bg-gray-300 rounded-lg overflow-hidden relative" data-id="element-91" data-mp="32asul4hi1775">
                                      <div className="w-full h-32 md:h-40" data-id="element-92" data-mp="2xdajp8jx8hwi"></div>
                                      <div className="absolute inset-0 flex flex-col justify-center p-4 md:p-6 text-gray-800" data-id="element-93" data-mp="3gykjf9tfderc">
                                        <span className="text-xs font-bold bg-white text-gray-600 px-2 py-0.5 rounded-full w-max mb-2" data-id="element-94" data-mp="3lwcgsjcu9t2x">
                                          DIMENSIONS
                                        </span>
                                        <h3 className="text-base md:text-lg font-bold mb-1" data-id="element-95" data-mp="3c1at7ry7oz2g">
                                          1/3 WIDTH BANNER
                                        </h3>
                                        <p className="text-xs md:text-sm" data-id="element-96" data-mp="330mf6x8tiypk">
                                          ASPECT RATIO 3:1
                                        </p>
                                      </div>
                                    </div>
                                    <div className="bg-gray-300 rounded-lg overflow-hidden relative" data-id="element-97" data-mp="2fa08paegpmho">
                                      <div className="w-full h-32 md:h-40" data-id="element-98" data-mp="2j990watutcb4"></div>
                                      <div className="absolute inset-0 flex flex-col justify-center p-4 md:p-6 text-gray-800" data-id="element-99" data-mp="2zqbf9ha5yn08">
                                        <span className="text-xs font-bold bg-white text-gray-600 px-2 py-0.5 rounded-full w-max mb-2" data-id="element-100" data-mp="2uri1dnqystzj">
                                          DIMENSIONS
                                        </span>
                                        <h3 className="text-base md:text-lg font-bold mb-1" data-id="element-101" data-mp="2kv5bz22gd2al">
                                          1/3 WIDTH BANNER
                                        </h3>
                                        <p className="text-xs md:text-sm" data-id="element-102" data-mp="2bymh3kanv4bd">
                                          ASPECT RATIO 3:1
                                        </p>
                                      </div>
                                    </div>
                                    <div className="bg-gray-300 rounded-lg overflow-hidden relative" data-id="element-103" data-mp="1x1wx5dddt6jf">
                                      <div className="w-full h-32 md:h-40" data-id="element-104" data-mp="1t24v7jihtyn1"></div>
                                      <div className="absolute inset-0 flex flex-col justify-center p-4 md:p-6 text-gray-800" data-id="element-105" data-mp="8iixqx5xfifz">
                                        <span className="text-xs font-bold bg-white text-gray-600 px-2 py-0.5 rounded-full w-max mb-2" data-id="element-106" data-mp="cjcl9pfgiv8c">
                                          DIMENSIONS
                                        </span>
                                        <h3 className="text-base md:text-lg font-bold mb-1" data-id="element-107" data-mp="3l190dcp0b85q">
                                          1/3 WIDTH BANNER
                                        </h3>
                                        <p className="text-xs md:text-sm" data-id="element-108" data-mp="3uz577sawvgy1">
                                          ASPECT RATIO 3:1
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <LazySection data-id="element-109" data-mp="377hq223gbk8y">
                                <div id="trending-now" className="py-1 md:py-4" data-id="element-110" data-mp="2gz5aetue5lmr">
                                  <Suspense fallback={<SectionFallback data-id="element-112" data-mp="2lz0392xq8lag" />} data-id="element-111" data-mp="2lyhzll8qj4si">
                                    <TrendingProducts data-id="element-113" data-mp="2lwdx97qhiduc" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <div className="py-1 md:py-4" data-id="element-114" data-mp="2byr3jf4j0hqb">
                                <div className="container mx-auto px-4" data-id="element-115" data-mp="1y8vz8ugmmjix">
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4" data-id="element-116" data-mp="22zvzo9oyfjq6">
                                    <div className="bg-gray-300 rounded-lg overflow-hidden relative" data-id="element-117" data-mp="1ob9j64nlclxd">
                                      <div className="w-full h-32 md:h-40" data-id="element-118" data-mp="1t9t54526pxpq"></div>
                                      <div className="absolute inset-0 flex flex-col justify-center p-4 md:p-6 text-gray-800" data-id="element-119" data-mp="1t7q46pzhuk68">
                                        <span className="text-xs font-bold bg-white text-gray-600 px-2 py-0.5 rounded-full w-max mb-2" data-id="element-120" data-mp="1k9wa7lp7c2m8">
                                          DIMENSIONS
                                        </span>
                                        <h3 className="text-base md:text-lg font-bold mb-1" data-id="element-121" data-mp="vq2oxl50nvzk">
                                          1/3 WIDTH BANNER
                                        </h3>
                                        <p className="text-xs md:text-sm" data-id="element-122" data-mp="15l4egi37f02o">
                                          ASPECT RATIO 3:1
                                        </p>
                                      </div>
                                    </div>
                                    <div className="bg-gray-300 rounded-lg overflow-hidden relative" data-id="element-123" data-mp="1tcrz4i9vhewi">
                                      <div className="w-full h-32 md:h-40" data-id="element-124" data-mp="1ocnj0tcvmm6t"></div>
                                      <div className="absolute inset-0 flex flex-col justify-center p-4 md:p-6 text-gray-800" data-id="element-125" data-mp="22cyb1zpxa6tk">
                                        <span className="text-xs font-bold bg-white text-gray-600 px-2 py-0.5 rounded-full w-max mb-2" data-id="element-126" data-mp="1xen1lq3jhybv">
                                          DIMENSIONS
                                        </span>
                                        <h3 className="text-base md:text-lg font-bold mb-1" data-id="element-127" data-mp="zzs86visspo3">
                                          1/3 WIDTH BANNER
                                        </h3>
                                        <p className="text-xs md:text-sm" data-id="element-128" data-mp="18t5a2myub1mb">
                                          ASPECT RATIO 3:1
                                        </p>
                                      </div>
                                    </div>
                                    <div className="bg-gray-300 rounded-lg overflow-hidden relative" data-id="element-129" data-mp="1nov1j0mso6yf">
                                      <div className="w-full h-32 md:h-40" data-id="element-130" data-mp="1rpoonrsexhq9"></div>
                                      <div className="absolute inset-0 flex flex-col justify-center p-4 md:p-6 text-gray-800" data-id="element-131" data-mp="3pgznlvhm4a18">
                                        <span className="text-xs font-bold bg-white text-gray-600 px-2 py-0.5 rounded-full w-max mb-2" data-id="element-132" data-mp="3tiuoiyvmsleh">
                                          DIMENSIONS
                                        </span>
                                        <h3 className="text-base md:text-lg font-bold mb-1" data-id="element-133" data-mp="3h5r0whqagzxq">
                                          1/3 WIDTH BANNER
                                        </h3>
                                        <p className="text-xs md:text-sm" data-id="element-134" data-mp="3852lbil8yriq">
                                          ASPECT RATIO 3:1
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <LazySection data-id="element-135" data-mp="3qx53wzhg8hvm">
                                <div className="py-1 md:py-4" data-id="element-136" data-mp="2khkzs4skspo7">
                                  <ProductSection title="Top Selling Products" viewAllLink="#" data-id="element-137" data-mp="2kiw2s2e75te9" />
                                </div>
                              </LazySection>
                              <div className="py-1 md:py-4" data-id="element-138" data-mp="c1a4cctlvwzv">
                                <div className="container mx-auto px-4" data-id="element-139" data-mp="3ueretfdpyby1">
                                  <div className="bg-gray-300 rounded-lg overflow-hidden relative" data-id="element-140" data-mp="3ucogm82exci8">
                                    <div className="w-full h-32 md:h-60" data-id="element-141" data-mp="28ch5tdo5jux"></div>
                                    <div className="absolute inset-0 flex flex-col justify-center p-4 md:p-8 text-gray-800" data-id="element-142" data-mp="q12qduu6tp1i">
                                      <span className="text-xs md:text-sm font-bold bg-white text-gray-600 px-2 md:px-3 py-1 rounded-full w-max mb-2 md:mb-4" data-id="element-143" data-mp="gzlg4t3gohkq">
                                        DIMENSIONS
                                      </span>
                                      <h2 className="text-xl md:text-4xl font-bold mb-1 md:mb-2" data-id="element-144" data-mp="31p0yczom6xyr">
                                        FULL WIDTH BANNER
                                      </h2>
                                      <p className="text-sm md:text-2xl font-semibold mb-2 md:mb-4" data-id="element-145" data-mp="2y6liqgg8f8ai">
                                        ASPECT RATIO 21:9
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <LazySection data-id="element-146" data-mp="2fh5d5epowpn8">
                                <div id="recently-viewed" className="py-1 md:py-4" data-id="element-147" data-mp="2fi6mner2542u">
                                  <ProductSection title="Recently Viewed" viewAllLink="#" data-id="element-148" data-mp="2p89qk9zpw2fy" />
                                </div>
                              </LazySection>
                              <LazySection data-id="element-149" data-mp="1rscfndtf69qi">
                                <div className="py-1 md:py-4" data-id="element-150" data-mp="1rrkhbv8ftxwj">
                                  <Suspense fallback={<SectionFallback data-id="element-152" data-mp="3rtamn3uqh8je" />} data-id="element-151" data-mp="3ruveglpnyddk">
                                    <FAQ data-id="element-153" data-mp="nixa6os5zxn" />
                                  </Suspense>
                                </div>
                              </LazySection>
                            </PullToRefresh>
                          </main>
                          <Footer data-id="element-154" data-mp="9et0yqtincg2" />
                          <MobileFooterNav data-id="element-155" data-mp="9hxbs71hl6fp" />
                          <PharmacistChat data-id="element-156" data-mp="38vzjh85zduyo" />
                          <div className="h-20 md:hidden" data-id="element-157" data-mp="38xarxladfqw4"></div>
                          <button className="hidden md:block fixed bottom-20 md:bottom-6 right-4 md:right-6 bg-[#1E2B55] text-white p-3 rounded-full shadow-lg hover:bg-[#162041] transition-all active:scale-95" onClick={scrollToTop} aria-label="Scroll to top" data-id="element-158" data-mp="3dvcbe7bpls84">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-159" data-mp="1248lc772mhon">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" data-id="element-160" data-mp="12hu3n01utcrp" />
                            </svg>
                          </button>
                          <AuthModal isOpen={authModalOpen} onClose={handleAuthModalClose} data-id="element-161" data-mp="ol71miv1nwex" />
                          <LocationModal isOpen={locationModalOpen} onClose={handleLocationModalClose} onSelectLocation={handleLocationSelect} data-id="element-162" data-mp="fk0mad44srn9" />
                          <AppDownloadBottomSheet isOpen={showAppDownload} onClose={handleAppDownloadClose} onContinue={handleAppDownloadContinue} data-id="element-163" data-mp="2t4oxmf7aask3" />
                          <LocationPermissionModal isOpen={showLocationPermission} onClose={handleLocationPermissionClose} onAllow={handleLocationPermissionAllow} data-id="element-164" data-mp="2jdb3yo20kmyv" />
                        </div>
                      </motion.div>} data-id="element-10" data-mp="3hcjrc6kp959o" />
                  <Route path="/checkout" element={<motion.div initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -20
                }} transition={{
                  duration: 0.2
                }} data-id="element-166" data-mp="25gfifw4kt31x">
                        <CheckoutPage data-id="element-167" data-mp="hu1llvlw7kce" />
                      </motion.div>} data-id="element-165" data-mp="20huo2dc09dle" />
                  <Route path="/thank-you" element={<motion.div initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.95
                }} transition={{
                  duration: 0.2
                }} data-id="element-169" data-mp="rk4g608nx6cu">
                        <ThankYouPage data-id="element-170" data-mp="1dj3g1rcvd4n3" />
                      </motion.div>} data-id="element-168" data-mp="whwgnindi0sn" />
                  <Route path="/brand/:brandSlug" element={<motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-172" data-mp="1ngrn9grnw4m5">
                        <BrandStorePage data-id="element-173" data-mp="269vgj10ywfg8" />
                      </motion.div>} data-id="element-171" data-mp="1rimj45qtppsj" />
                </Routes>
              </AnimatePresence>
            </Suspense>
          </div>
        </HelmetProvider>
      </QueryClientProvider>
    </ErrorBoundary>;
}